import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CursorPaginatedResponse,
  EmailTemplate,
  EmailTemplateListData,
  EmailTemplateListParams,
  SMSTemplate,
  SMSTemplateListData,
  SMSTemplateListParams,
} from '@/libs/interfaces'
import { communicationApiMock } from '@/libs/mocks/handlers/communication'

const communicationApiReal = {
  // Email Templates
  createEmailTemplate: (data: Partial<EmailTemplate>): Promise<EmailTemplate> =>
    api
      .post<ApiResponse<EmailTemplate>>(URLs.EmailTemplates.create(data.orgID || ''), data)
      .then((response) => response.data.data),
  deleteEmailTemplate: async (orgID: string, emailTemplateID: string) =>
    api.delete(URLs.EmailTemplates.byID(orgID || '', emailTemplateID || '')),
  getEmailTemplate: (orgID: string, emailTemplateID: string): Promise<EmailTemplate> =>
    api
      .get<ApiResponse<EmailTemplate>>(URLs.EmailTemplates.byID(orgID, emailTemplateID))
      .then((response) => response.data.data),
  listEmailTemplatesCursor: async (
    params: EmailTemplateListParams,
  ): Promise<CursorPaginatedResponse<EmailTemplate>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<EmailTemplateListData>>(URLs.EmailTemplates.list(orgID, query))
      .then((response) => response?.data?.data)
    return {
      data: response?.data || [],
      pageInfo: {
        first: response?.first,
        prev: response?.prev,
        cursor: response?.cursor,
        next: response?.next,
        last: response?.last,
      },
    }
  },
  updateEmailTemplate: async (payload: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    const response = await api.patch<ApiResponse<EmailTemplate>>(
      URLs.EmailTemplates.byID(payload?.orgID || '', payload.emailTemplateID || ''),
      payload,
    )
    return response.data?.data || {}
  },

  // SMS Templates
  createSMSTemplate: (data: Partial<SMSTemplate>): Promise<SMSTemplate> =>
    api
      .post<ApiResponse<SMSTemplate>>(URLs.SMSTemplates.create(data.orgID || ''), data)
      .then((response) => response.data.data),
  deleteSMSTemplate: async (orgID: string, smsTemplateID: string) =>
    api.delete(URLs.SMSTemplates.byID(orgID || '', smsTemplateID || '')),
  getSmsTemplate: (orgID: string, smsTemplateID: string): Promise<SMSTemplate> =>
    api
      .get<ApiResponse<SMSTemplate>>(URLs.SMSTemplates.byID(orgID, smsTemplateID))
      .then((response) => response.data.data),
  listSMSTemplatesCursor: async (
    params: SMSTemplateListParams,
  ): Promise<CursorPaginatedResponse<SMSTemplate>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<SMSTemplateListData>>(URLs.SMSTemplates.list(orgID, query))
      .then((response) => response?.data?.data)
    return {
      data: response?.data || [],
      pageInfo: {
        first: response?.first,
        prev: response?.prev,
        cursor: response?.cursor,
        next: response?.next,
        last: response?.last,
      },
    }
  },
  updateSmsTemplate: async (payload: Partial<SMSTemplate>): Promise<SMSTemplate> => {
    const response = await api.patch<ApiResponse<SMSTemplate>>(
      URLs.SMSTemplates.byID(payload?.orgID || '', payload.smsTemplateID || ''),
      payload,
    )
    return response.data?.data || {}
  },
}

export const communicationApi: typeof communicationApiReal = __IS_MOCK_MODE__
  ? communicationApiMock
  : communicationApiReal
